// Package imports:
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { faTrashCan } from '@fortawesome/pro-solid-svg-icons';
import { Toast, ToastContainer } from 'react-bootstrap';
import Bugsnag from '@bugsnag/js';

// UI element imports:
import Button from '../../../ui-elements/Button/Button';
import Input from '../../../ui-elements/Input/Input';
import DropSelect from '../../../ui-elements/DropSelect/DropSelect';
import Link from '../../../ui-elements/Link/Link';
import GoBackLink from '../../../ui-elements/GoBackLink/GoBackLink';
import Toggle from '../../../ui-elements/Toggle/Toggle';
import Alert, { AlertType } from '../../../ui-elements/Alert/Alert';
import Modal from '../../../ui-elements/Modal/Modal';

// Service imports:
import { TOAST_DELAY } from '../../../services/config';

// Type imports:
import { ServiceCompanySettings } from '../../../types/DueDiligenceTypes';

// Component imports:
import KYCSettingsBannerUpload from './KYCSettingsBannerUpload';
import KYCSettingsEmailPreview from './KYCSettingsEmailPreview';
import CustomPageSettings from './CustomPageSettings';
import { ImagePreview } from './ImageUploadField';

const FREQUENCY_OPTIONS = [
    { label: '24 mánuðir', value: '24' },
    { label: '18 mánuðir', value: '18' },
    { label: '12 mánuðir', value: '12' },
    { label: '6 mánuðir', value: '6' },
    { label: 'Aldrei', value: '0' },
];

interface BannerPreview {
    url: string;
    name: string;
    width: number;
    height: number;
    size: number;
}

interface SettingsValues {
    email: string;
    lowRiskFrequency: string | null;
    mediumRiskFrequency: string | null;
    highRiskFrequency: string | null;
    matchRate: string;
    autoLookupEnabled: boolean;
    hasBannerFile: boolean;
    // Custom page
    customPageSlug: string;
    primaryColor: string;
    secondaryColor: string;
    accentColor: string;
    hasCustomLogo: boolean;
    hasBackground: boolean;
}

const DEFAULT_VALUES: SettingsValues = {
    email: '',
    lowRiskFrequency: '24',
    mediumRiskFrequency: '24',
    highRiskFrequency: '24',
    matchRate: '85',
    autoLookupEnabled: false,
    hasBannerFile: false,
    customPageSlug: '',
    primaryColor: '',
    secondaryColor: '',
    accentColor: '',
    hasCustomLogo: false,
    hasBackground: false,
};

interface IKYCSettingsProps {
    onGoBack: () => void;
    hasCustomWebPage: boolean;
}

const KYCSettings: React.FC<IKYCSettingsProps> = ({ onGoBack, hasCustomWebPage }) => {
    // Áminningar
    const [email, setEmail] = useState('');

    // Tíðni áminninga
    const [lowRiskFrequency, setLowRiskFrequency] = useState<string | null>('24');
    const [mediumRiskFrequency, setMediumRiskFrequency] = useState<string | null>('24');
    const [highRiskFrequency, setHighRiskFrequency] = useState<string | null>('24');

    // Alþjóðlegir válistar
    const [matchRate, setMatchRate] = useState('85');

    // Sjálfvirk uppfletting
    const [autoLookupEnabled, setAutoLookupEnabled] = useState(false);

    // Banner upload
    const [bannerFile, setBannerFile] = useState<File | null>(null);
    const [bannerPreview, setBannerPreview] = useState<BannerPreview | null>(null);
    const [bannerError, setBannerError] = useState('');

    // Custom page fields
    const [customPageSlug, setCustomPageSlug] = useState('');
    const [primaryColor, setPrimaryColor] = useState('');
    const [secondaryColor, setSecondaryColor] = useState('');
    const [accentColor, setAccentColor] = useState('');
    const [customLogoFile, setCustomLogoFile] = useState<File | null>(null);
    const [customLogoPreview, setCustomLogoPreview] = useState<ImagePreview | null>(null);
    const [customLogoError, setCustomLogoError] = useState('');
    const [backgroundFile, setBackgroundFile] = useState<File | null>(null);
    const [backgroundPreview, setBackgroundPreview] = useState<ImagePreview | null>(null);
    const [backgroundError, setBackgroundError] = useState('');

    // Save state
    const [saving, setSaving] = useState(false);

    // Toast state
    const [toastShow, setToastShow] = useState(false);
    const [toastType, setToastType] = useState<AlertType>('success');
    const [toastMessage, setToastMessage] = useState('');

    // Unsaved changes modal
    const [showUnsavedModal, setShowUnsavedModal] = useState(false);

    // Loading state
    const [, setLoading] = useState(true);

    // Track initial/saved values for dirty detection
    const initialValues = useRef<SettingsValues>({ ...DEFAULT_VALUES });

    // Scroll to top on mount
    useEffect(() => { window.scrollTo(0, 0); }, []);

    // Fetch initial settings on mount
    useEffect(() => {
        let mounted = true;
        const fetchSettings = async () => {
            try {
                const response = await fetch('/DueDiligence/GetServiceCompanySettings', {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json' },
                });
                if (response.ok) {
                    const data: ServiceCompanySettings = await response.json();
                    if (!mounted) return;
                    setEmail(data.email ?? '');

                    const hasSavedBanner = data.logo != null;
                    if (hasSavedBanner) {
                        const mimeType = data.logo!.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
                        const dataUrl = `data:${mimeType};base64,${data.logo}`;
                        // base64: every 4 chars = 3 bytes; subtract padding
                        const padding = (data.logo!.match(/={1,2}$/) ?? [''])[0].length;
                        const sizeBytes = Math.floor(data.logo!.length * 3 / 4) - padding;
                        const img = new Image();
                        img.onload = () => {
                            if (!mounted) return;
                            setBannerPreview({ url: dataUrl, name: 'Saved banner', width: img.naturalWidth, height: img.naturalHeight, size: sizeBytes });
                        };
                        img.src = dataUrl;
                    }

                    // Custom page fields
                    setCustomPageSlug(data.customPageSlug ?? '');
                    setPrimaryColor(data.primaryColor ?? '');
                    setSecondaryColor(data.secondaryColor ?? '');
                    setAccentColor(data.accentColor ?? '');

                    const hasSavedCustomLogo = data.customLogo != null;
                    if (hasSavedCustomLogo) {
                        const logoMime = data.customLogo!.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
                        const logoDataUrl = `data:${logoMime};base64,${data.customLogo}`;
                        const logoPadding = (data.customLogo!.match(/={1,2}$/) ?? [''])[0].length;
                        const logoSize = Math.floor(data.customLogo!.length * 3 / 4) - logoPadding;
                        const logoImg = new Image();
                        logoImg.onload = () => {
                            if (!mounted) return;
                            setCustomLogoPreview({ url: logoDataUrl, name: 'Saved logo', width: logoImg.naturalWidth, height: logoImg.naturalHeight, size: logoSize });
                        };
                        logoImg.src = logoDataUrl;
                    }

                    const hasSavedBackground = data.backgroundImage != null;
                    if (hasSavedBackground) {
                        const bgMime = data.backgroundImage!.startsWith('/9j/') ? 'image/jpeg' : 'image/png';
                        const bgDataUrl = `data:${bgMime};base64,${data.backgroundImage}`;
                        const bgPadding = (data.backgroundImage!.match(/={1,2}$/) ?? [''])[0].length;
                        const bgSize = Math.floor(data.backgroundImage!.length * 3 / 4) - bgPadding;
                        const bgImg = new Image();
                        bgImg.onload = () => {
                            if (!mounted) return;
                            setBackgroundPreview({ url: bgDataUrl, name: 'Saved background', width: bgImg.naturalWidth, height: bgImg.naturalHeight, size: bgSize });
                        };
                        bgImg.src = bgDataUrl;
                    }

                    initialValues.current = {
                        ...initialValues.current,
                        email: data.email ?? '',
                        hasBannerFile: hasSavedBanner,
                        customPageSlug: data.customPageSlug ?? '',
                        primaryColor: data.primaryColor ?? '',
                        secondaryColor: data.secondaryColor ?? '',
                        accentColor: data.accentColor ?? '',
                        hasCustomLogo: hasSavedCustomLogo,
                        hasBackground: hasSavedBackground,
                    };
                }
            } catch (error) {
                Bugsnag.notify(error as Error);
            } finally {
                if (mounted) setLoading(false);
            }
        };
        fetchSettings();
        return () => { mounted = false; };
    }, []);

    const getFrequencyLabel = (val: string | null): string => {
        const option = FREQUENCY_OPTIONS.find((o) => o.value === val);
        return option ? option.label : val ?? '–';
    };

    const getChangedFields = useCallback((): { label: string; oldValue: string; newValue: string }[] => {
        const changes: { label: string; oldValue: string; newValue: string }[] = [];
        const init = initialValues.current;
        if (email !== init.email)
            changes.push({ label: 'Netfang', oldValue: init.email || '–', newValue: email || '–' });
        if (lowRiskFrequency !== init.lowRiskFrequency)
            changes.push({ label: 'Tíðni áminninga – lítil áhætta', oldValue: getFrequencyLabel(init.lowRiskFrequency), newValue: getFrequencyLabel(lowRiskFrequency) });
        if (mediumRiskFrequency !== init.mediumRiskFrequency)
            changes.push({ label: 'Tíðni áminninga – meðal áhætta', oldValue: getFrequencyLabel(init.mediumRiskFrequency), newValue: getFrequencyLabel(mediumRiskFrequency) });
        if (highRiskFrequency !== init.highRiskFrequency)
            changes.push({ label: 'Tíðni áminninga – mikil áhætta', oldValue: getFrequencyLabel(init.highRiskFrequency), newValue: getFrequencyLabel(highRiskFrequency) });
        if (matchRate !== init.matchRate)
            changes.push({ label: 'Samsvörun (match rate)', oldValue: `${init.matchRate}%`, newValue: `${matchRate}%` });
        if (autoLookupEnabled !== init.autoLookupEnabled)
            changes.push({ label: 'Sjálfvirk uppfletting', oldValue: init.autoLookupEnabled ? 'Virkt' : 'Óvirkt', newValue: autoLookupEnabled ? 'Virkt' : 'Óvirkt' });
        const bannerRemoved = init.hasBannerFile && bannerPreview === null && bannerFile === null;
        if (bannerFile !== null || bannerRemoved)
            changes.push({ label: 'Borði tölvupósts', oldValue: init.hasBannerFile ? 'Með borða' : 'Enginn borði', newValue: bannerFile !== null ? 'Með borða' : 'Enginn borði' });
        // Custom page
        if (customPageSlug !== init.customPageSlug)
            changes.push({ label: 'Slóð sérsniðinnar síðu', oldValue: init.customPageSlug || '–', newValue: customPageSlug || '–' });
        if (primaryColor !== init.primaryColor)
            changes.push({ label: 'Aðallitur', oldValue: init.primaryColor || '–', newValue: primaryColor || '–' });
        if (secondaryColor !== init.secondaryColor)
            changes.push({ label: 'Aukalitur (bakgrunnur)', oldValue: init.secondaryColor || '–', newValue: secondaryColor || '–' });
        if (accentColor !== init.accentColor)
            changes.push({ label: 'Áherslulitur (takkar o.fl.)', oldValue: init.accentColor || '–', newValue: accentColor || '–' });
        const customLogoRemoved = init.hasCustomLogo && customLogoPreview === null && customLogoFile === null;
        if (customLogoFile !== null || customLogoRemoved)
            changes.push({ label: 'Lógó fyrirtækis', oldValue: init.hasCustomLogo ? 'Með lógó' : 'Ekkert lógó', newValue: customLogoFile !== null ? 'Með lógó' : 'Ekkert lógó' });
        const backgroundRemoved = init.hasBackground && backgroundPreview === null && backgroundFile === null;
        if (backgroundFile !== null || backgroundRemoved)
            changes.push({ label: 'Bakgrunnsmynd', oldValue: init.hasBackground ? 'Með mynd' : 'Engin mynd', newValue: backgroundFile !== null ? 'Með mynd' : 'Engin mynd' });
        return changes;
    }, [email, lowRiskFrequency, mediumRiskFrequency, highRiskFrequency, matchRate, autoLookupEnabled, bannerFile, bannerPreview, customPageSlug, primaryColor, secondaryColor, accentColor, customLogoFile, customLogoPreview, backgroundFile, backgroundPreview]);

    const isDirty = getChangedFields().length > 0;

    const handleSave = useCallback(async () => {
        setSaving(true);
        try {
            const formData = new FormData();
            formData.append('email', email);

            if (bannerFile) {
                formData.append('banner', bannerFile);
            } else if (initialValues.current.hasBannerFile && bannerPreview === null) {
                formData.append('removeBanner', 'true');
            }

            // Custom page fields
            formData.append('customPageSlug', customPageSlug);
            formData.append('primaryColor', primaryColor);
            formData.append('secondaryColor', secondaryColor);
            formData.append('accentColor', accentColor);

            if (customLogoFile) {
                formData.append('customLogoFile', customLogoFile);
            } else if (initialValues.current.hasCustomLogo && customLogoPreview === null) {
                formData.append('removeCustomLogo', 'true');
            }

            if (backgroundFile) {
                formData.append('backgroundFile', backgroundFile);
            } else if (initialValues.current.hasBackground && backgroundPreview === null) {
                formData.append('removeBackground', 'true');
            }

            const response = await fetch('/DueDiligence/UpdateServiceCompanySettings', {
                method: 'PUT',
                body: formData,
            });

            if (!response.ok) {
                const reason = await response.text().catch(() => '');
                throw new Error(reason || 'Failed to save settings');
            }

            // Reset baseline after successful save
            initialValues.current = {
                email,
                lowRiskFrequency,
                mediumRiskFrequency,
                highRiskFrequency,
                matchRate,
                autoLookupEnabled,
                hasBannerFile: bannerPreview !== null,
                customPageSlug,
                primaryColor,
                secondaryColor,
                accentColor,
                hasCustomLogo: customLogoPreview !== null,
                hasBackground: backgroundPreview !== null,
            };

            // Clear file objects so dirty detection no longer sees them as pending uploads
            setBannerFile(null);
            setCustomLogoFile(null);
            setBackgroundFile(null);

            setToastType('success');
            setToastMessage('Stillingar vistaðar');
            setToastShow(true);
        } catch (error) {
            Bugsnag.notify(error as Error);
            setToastType('error');
            const msg = error instanceof Error ? error.message : '';
            setToastMessage(msg || 'Villa kom upp við að vista stillingar');
            setToastShow(true);
        } finally {
            setSaving(false);
        }
    }, [email, lowRiskFrequency, mediumRiskFrequency, highRiskFrequency, matchRate, autoLookupEnabled, bannerFile, bannerPreview, customPageSlug, primaryColor, secondaryColor, accentColor, customLogoFile, customLogoPreview, backgroundFile, backgroundPreview]);

    const handleDeleteBanner = useCallback(() => {
        setBannerFile(null);
        setBannerPreview(null);
        setBannerError('');
    }, []);

    const handleGoBackClick = useCallback(() => {
        if (isDirty) {
            setShowUnsavedModal(true);
        } else {
            onGoBack();
        }
    }, [isDirty, onGoBack]);

    const handleResetCustomPage = useCallback(() => {
        setCustomPageSlug('');
        setPrimaryColor('');
        setSecondaryColor('');
        setAccentColor('');
        setCustomLogoFile(null);
        setCustomLogoPreview(null);
        setCustomLogoError('');
        setBackgroundFile(null);
        setBackgroundPreview(null);
        setBackgroundError('');
    }, []);

    return (
        <div className="KCL_kyc-settings">
            <div className="shell">
                <div className="kyc-settings__header">
                    <GoBackLink handleGoBackOverride={handleGoBackClick} />
                    <div className="kyc-settings__title-row">
                        <h1>Stillingar</h1>
                        <Button
                            buttonType="primary"
                            onClick={handleSave}
                            showLoader={saving}
                            disabled={saving || !isDirty}
                            className="kyc-settings__save-btn--mobile"
                        >
                            Vista breytingar
                        </Button>
                    </div>
                </div>

                <div className="kyc-settings__columns">
                    {/* Left column */}
                    <div className="kyc-settings__column kyc-settings__column--left">
                        {/* Áminningar */}
                        <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Áminningar</h4>
                            <p className="kyc-settings__section-text">
                                Áminningar eru sendar sjálfvirkt úr KYC kerfinu þegar hámarks tími er liðinn frá síðustu könnun.
                                Hér er hægt að skrá netfang fyrir allar áminningar og aðrar tilkynningar sem berast úr kerfinu:
                            </p>
                            <Input
                                label="Netfang"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                fullWidth
                            />
                        </div>

                        {/* Tíðni áminninga */}
                        <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Tíðni áminninga</h4>
                            <p className="kyc-settings__section-text">
                                Hér er hægt að stilla <strong>tíðni áminninga</strong> sem sendar eru þegar tími er kominn á nýja könnun.
                                <br />
                                Athugið að áhættumeiri viðskiptavinir ættu að sæta tíðari uppfærslu áreiðanleikakönnunar.
                            </p>
                            <div className="kyc-settings__frequency-rows">
                                <div className="kyc-settings__frequency-row">
                                    <div className="kyc-settings__risk-badge-wrapper">
                                        <span className="kyc-settings__risk-badge kyc-settings__risk-badge--low">
                                            Lítil áhætta
                                        </span>
                                    </div>
                                    <DropSelect
                                        options={FREQUENCY_OPTIONS}
                                        defaultValue={lowRiskFrequency}
                                        onChange={(val) => setLowRiskFrequency(val as string)}
                                        size="lg"
                                        className="kyc-settings__frequency-select"
                                        disabled
                                    />
                                </div>
                                <div className="kyc-settings__frequency-row">
                                    <div className="kyc-settings__risk-badge-wrapper">
                                        <span className="kyc-settings__risk-badge kyc-settings__risk-badge--medium">
                                            Meðal áhætta
                                        </span>
                                    </div>
                                    <DropSelect
                                        options={FREQUENCY_OPTIONS}
                                        defaultValue={mediumRiskFrequency}
                                        onChange={(val) => setMediumRiskFrequency(val as string)}
                                        size="lg"
                                        className="kyc-settings__frequency-select"
                                        disabled
                                    />
                                </div>
                                <div className="kyc-settings__frequency-row">
                                    <div className="kyc-settings__risk-badge-wrapper">
                                        <span className="kyc-settings__risk-badge kyc-settings__risk-badge--high">
                                            Mikil áhætta
                                        </span>
                                    </div>
                                    <DropSelect
                                        options={FREQUENCY_OPTIONS}
                                        defaultValue={highRiskFrequency}
                                        onChange={(val) => setHighRiskFrequency(val as string)}
                                        size="lg"
                                        className="kyc-settings__frequency-select"
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Alþjóðlegir válistar */}
                        <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Alþjóðlegir válistar</h4>
                            <p className="kyc-settings__section-text">
                                KYC kerfið flettir sjálfkrafa upp einstaklingum á móti alþjóðlegum válistum,
                                þvingunarlistum, PEP og neikvæðri umfjöllun (e. adverse media). Leitað er eftir
                                nafni, fæðingardegi og þjóðerni. Hér er hægt að stilla samsvörun (e. match rate)
                                niðurstaðna, það er hvaða líkindi þurfa að vera til staðar til að niðurstaða
                                teljist viðeigandi. Lágmarks samsvörun er 85%.
                            </p>
                            <div className="kyc-settings__match-rate-row">
                                <span className="kyc-settings__match-rate-label">
                                    Samsvörun (e. match rate)
                                </span>
                                <div className="kyc-settings__match-rate-input-wrapper">
                                    <Input
                                        inputType="percentage"
                                        value={matchRate}
                                        onChange={(e) => setMatchRate(e.target.value)}
                                        percentageMin={85}
                                        percentageMax={100}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Sjálfvirk uppfletting */}
                        <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Sjálfvirk uppfletting</h4>
                            <p className="kyc-settings__section-text">
                                Hér er hægt að virkja sjálfvirka uppflettingu á þeim <strong>lögaðilum</strong> sem
                                áreiðanleikakönnun er framkvæmd á. Sjálfvirk uppfletting er þá gerð framvegis,
                                samhliða öðrum uppflettingum á viðskiptavini.
                            </p>
                            <Toggle
                                toggled={autoLookupEnabled}
                                onToggle={setAutoLookupEnabled}
                                disabled
                                label="Virkja sjálfvirka uppflettingu á lögaðilum á móti alþjóðlegum válistum"
                            />
                            <p className="kyc-settings__section-text">
                                Athugaðu að rukkað er sérstaklega fyrir hverja uppflettingu samkvæmt{' '}
                                <Link url="/Verdskra">verðskrá Keldunnar.</Link>
                            </p>
                        </div>
                    </div>

                    {/* Right column */}
                    {/* TODO: Uncomment when backend supports banner upload */}
                    <div className="kyc-settings__column kyc-settings__column--right">
                        <div className="kyc-settings__section">
                            <h4 className="kyc-settings__section-title">Sérsniðið útlit tölvupósta</h4>
                            <p className="kyc-settings__section-text">
                                Hér er hægt að hlaða upp sérsniðnum borða sem birtist í haus tölvupósta sem
                                sendir eru til viðskiptavina úr KYC kerfinu.
                            </p>
                            <p className="kyc-settings__section-text">
                                Hér að neðan er forskoðun á tölvupóstinum eins og hann mun líta út.
                                Borðinn birtist efst í tölvupóstinum:
                            </p>

                            <KYCSettingsEmailPreview
                                bannerSrc={bannerPreview && !bannerError ? bannerPreview.url : undefined}
                            />

                            <p className="kyc-settings__section-text">
                                Athugið að borðinn þarf að vera í eftirfarandi stærð:
                            </p>
                            <div className="kyc-settings__size-requirements">
                                <div className="kyc-settings__size-item">
                                    <span className="kyc-settings__size-bullet" />
                                    <strong>Breidd: 600 px</strong>
                                </div>
                                <div className="kyc-settings__size-item">
                                    <span className="kyc-settings__size-bullet" />
                                    <strong>Hæð: allt að 300 px</strong>
                                </div>
                                <div className="kyc-settings__size-item">
                                    <span className="kyc-settings__size-bullet" />
                                    <strong>Hámarks þyngd: 150 kb</strong>
                                </div>
                            </div>

                            <KYCSettingsBannerUpload
                                bannerPreview={bannerPreview}
                                setBannerPreview={setBannerPreview}
                                bannerError={bannerError}
                                setBannerError={setBannerError}
                                setBannerFile={setBannerFile}
                            />

                            {bannerPreview && !bannerError && (
                                <div className="kyc-settings__banner-info">
                                    <img
                                        src={bannerPreview.url}
                                        alt="Banner preview"
                                        className="kyc-settings__banner-preview-img"
                                    />
                                    <div className="kyc-settings__banner-meta">
                                        <span>{bannerPreview.width} x {bannerPreview.height} px</span>
                                        <span>{Math.round(bannerPreview.size / 1024)} kb</span>
                                        <Button
                                            buttonType="secondary"
                                            size="sm"
                                            icon={faTrashCan}
                                            onClick={handleDeleteBanner}
                                        >
                                            Eyða mynd
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {bannerError && bannerPreview && (
                                <div className="kyc-settings__banner-error-info">
                                    <div className="kyc-settings__banner-meta kyc-settings__banner-meta--error">
                                        <span>{bannerPreview.width} x {bannerPreview.height} px</span>
                                        <span>{Math.round(bannerPreview.size / 1024)} kb</span>
                                    </div>
                                    <p className="kyc-settings__banner-error-text">
                                        {bannerError}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Custom Page Settings — full width below columns */}
                <div className="kyc-settings__column kyc-settings__column--full">
                    {hasCustomWebPage ? (
                        <CustomPageSettings
                            slug={customPageSlug}
                            setSlug={setCustomPageSlug}
                            primaryColor={primaryColor}
                            setPrimaryColor={setPrimaryColor}
                            secondaryColor={secondaryColor}
                            setSecondaryColor={setSecondaryColor}
                            accentColor={accentColor}
                            setAccentColor={setAccentColor}
                            customLogoPreview={customLogoPreview}
                            setCustomLogoPreview={setCustomLogoPreview}
                            customLogoError={customLogoError}
                            setCustomLogoError={setCustomLogoError}
                            setCustomLogoFile={setCustomLogoFile}
                            backgroundPreview={backgroundPreview}
                            setBackgroundPreview={setBackgroundPreview}
                            backgroundError={backgroundError}
                            setBackgroundError={setBackgroundError}
                            setBackgroundFile={setBackgroundFile}
                            onReset={handleResetCustomPage}
                        />
                    ) : (
                        <div className="kyc-settings__custom-page-upsell">
                            <h4 className="kyc-settings__section-title">Sérsniðinn vefur</h4>
                            <p className="kyc-settings__section-text">
                                Vefurinn birtist á slóðinni <strong>kyc.keldan.is/{'<þitt-fyrirtæki>'}</strong> og er hægt að stilla útlit í þinni hönnun, litum og lógói .
                                Hægt er að senda slóðina beint á viðskiptavini sem skrá sig inn með rafrænum skilríkjum og svara áreiðanleikakönnun — fyrir sig eða fyrir hönd félags.
                                Niðurstaðan er sú að viðskiptavinir stofnast sjálfkrafa í kerfinu með öll gögn útfyllt - það eina sem eftir situr er yfirferð og áhættumat.
                            </p>
                            <p className="kyc-settings__section-text">
                                Hafðu samband við{' '}
                                <a href="mailto:info@keldan.is" className="kyc-settings__upsell-link">info@keldan.is</a>{' '}
                                til að fá nánari upplýsingar og til virkja þessa þjónustu.
                            </p>
                        </div>
                    )}
                </div>

                {/* Desktop save button */}
                <div className="kyc-settings__footer">
                    <Button
                        buttonType="primary"
                        onClick={handleSave}
                        showLoader={saving}
                        disabled={saving || !isDirty}
                    >
                        Vista breytingar
                    </Button>
                </div>
            </div>

            {/* Unsaved changes modal */}
            <Modal
                show={showUnsavedModal}
                onHide={() => setShowUnsavedModal(false)}
                hasCloseButton
                header="Óvistaðar breytingar"
                body={
                    <>
                        <p>Þú ert með óvistaðar breytingar. Ertu viss um að þú viljir fara til baka?</p>
                        <ul className="kyc-settings__changes-list">
                            {getChangedFields().map((field) => (
                                <li key={field.label}>
                                    <strong>{field.label}:</strong>{' '}
                                    <span className="kyc-settings__change-old">{field.oldValue}</span>
                                    {' → '}
                                    <span className="kyc-settings__change-new">{field.newValue}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="modal__button-row">
                            <Button buttonType="secondary" onClick={() => setShowUnsavedModal(false)}>
                                Hætta við
                            </Button>
                            <Button buttonType="primary" onClick={() => { setShowUnsavedModal(false); onGoBack(); }}>
                                Fara til baka
                            </Button>
                        </div>
                    </>
                }
            />

            {/* Toast notifications */}
            <ToastContainer className="toastContainer">
                <Toast onClose={() => setToastShow(false)} autohide delay={TOAST_DELAY} show={toastShow}>
                    <Alert type={toastType} headText={toastMessage} />
                </Toast>
            </ToastContainer>
        </div>
    );
};

export default KYCSettings;
